import { Application } from '../application/application';
import { Container, ContainerDefinition, DEFAULT_DRAWER_WIDTH } from './cabinets';
import { Observable, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { delegatedObserver } from '../dom/dom';
import { throttle } from '../util/util';
import { topContainerDefinition } from './container-top';
import { Dialog, DialogDefinition } from './dialog';
import {getGMapImplementationWrapper, MapImplementationWrapper} from  '@mapp/r-mapp-lib';

export const DIALOG_ID_PREFIX = 'dlg-';

/**
 * The (render) options for the panel. 
 */
interface PanelOptions {
	/** Number of pixels to keep clear before the left edge of the panel meets the left end of the stage. */
	marginLeft: number,
	/** The minimum width that has to be visible when sliding a drawer to the right. For a width of 0 a drawer needs to be closed. */
	bumperWidth: number
}

/**
 * The ContainerPanel controls the top level div that holds
   the InspectionPanel. This class manages a list of Containers.
   Each Container has a set of cabinets. Each Cabinet has a set
   of Drawers. The ContainerPanel signals the containers when to
   build/load and manages all event handlers dealing with moving 
   and resizing of the drawers.
 */
export class ContainerPanel {

	public static SELECTOR_EVENT_BLOCKER_LAYER = 'body div.tlmd-event-blocker';

	public options: PanelOptions;

	//  private containers: Container[];
	private containers: { [key: string]: Container };
	private containerDefinitions: { [key: string]: ContainerDefinition };
	private dialogs: { [key: string]: Dialog };
	private dialogDefinitions: { [key: string]: DialogDefinition };

	private activeContainerId: string;
	private activeDialogId: string;

	private topElem: HTMLDivElement
	private topStyle: CSSStyleDeclaration;
	private stageElem: HTMLElement;
	private stageStyle: CSSStyleDeclaration;
	private mouseMoveObs: Observable<MouseEvent>;
	private mouseUpObs: Observable<MouseEvent>;
	private mouseMovesUntilUpObs: Observable<MouseEvent>;
	private touchEndObs: Observable<TouchEvent>;

	private eventBlockLayerElem: HTMLDivElement


	constructor(private topSelector: string, private application: Application, stageSelector?: string) {
		this.topElem = document.querySelector(topSelector);
		this.topStyle = window.getComputedStyle(this.topElem);

		this.stageElem = stageSelector ? <HTMLElement>document.querySelector(stageSelector) : document.body;
		this.stageStyle = window.getComputedStyle(this.stageElem);

		this.eventBlockLayerElem = document.querySelector(ContainerPanel.SELECTOR_EVENT_BLOCKER_LAYER);

		this.mouseUpObs = <Observable<MouseEvent>>fromEvent(window, 'mouseup');
		this.mouseMoveObs = <Observable<MouseEvent>>fromEvent(window, 'mousemove');
		this.mouseMovesUntilUpObs = this.mouseMoveObs.pipe(takeUntil(this.mouseUpObs));
		this.touchEndObs = <Observable<TouchEvent>>fromEvent(window, 'touchend');

		this.topElem.classList.add('tlmd-focus-panel');

		this.subscribeGeneralPurposeHandlers();


		this.activeContainerId = undefined;
		this.containerDefinitions = {};
		this.containers = {};

		this.activeDialogId = undefined;
		this.dialogDefinitions = {};
		this.dialogs = {};

		this.fixOptions();
	}

	public addDialog(dialogId: string, factory: string, dialogDefinition: DialogDefinition): Dialog {
		if (this.dialogDefinitions[dialogId] !== undefined) {
			//destruct existing dialog first, this will keep the markup as constructed in the else part
			this.dialogs[dialogId].release();
		} else {
			this.eventBlockLayerElem.insertAdjacentHTML('beforeend', `<div class="tlmd-dialog-panel ${dialogId} ${factory}" data-dialog="${dialogId}"></div>`);
		}
		this.dialogDefinitions[dialogId] = dialogDefinition;
		this.dialogs[dialogId] = (dialogDefinition.build ?
			//this allows for the construction of other Container (sub-classes) to be constructed
			dialogDefinition.build(this, dialogId, this.application)
			: new Dialog(this, dialogId, this.application))
			.setup(dialogDefinition);

		return this.dialogs[dialogId];
	}
	public removeDialog(dialogId: string): void {
		let dialog = this.dialogs[dialogId];
		if (dialog) {
			dialog.release();
			delete this.dialogDefinitions[dialogId];
			delete this.dialogs[dialogId];
		}
	}

	public addContainer(containerId: string, factory: string, containerDefinition: ContainerDefinition): Container {
		if (this.containerDefinitions[containerId] !== undefined) {
			//destruct existing container first, this will keep the markup as constructed in the else part
			this.containers[containerId].release();
		} else {
			this.topElem.insertAdjacentHTML('beforeend', `<div class="tlmd-container ${containerId} ${factory}" data-container="${containerId}"></div>`);
		}

		this.containerDefinitions[containerId] = containerDefinition;

		this.containers[containerId] = (containerDefinition.build ?
			//this allows for the construction of other Container (sub-classes) to be constructed
			containerDefinition.build(this, containerId, this.application)
			: new Container(this, containerId, this.application))
			.setup(containerDefinition);

		return this.containers[containerId];
	}
	public removeContainer(containerId: string): void {
		let container = this.containers[containerId];
		if (container) {
			container.destroy();
			delete this.containerDefinitions[containerId];
			delete this.containers[containerId];
		}
	}

	private subscribeGeneralPurposeHandlers(): void {
		//return to top menu
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-menu')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				this.switchContainer('top');
			});

		//hide the inspection panel
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-action-hide')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				this.getContainerElement(this.activeContainerId).style.display = 'none';
				this.containers[this.activeContainerId].onBlur();
			});

		//close a container
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-close')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				this.getContainer().close();

				let closedContainerId = this.activeContainerId;
				this.activeContainerId = undefined;
				//signal all containers that this container closed
				Object.keys(this.containers)
					.filter(containerId => containerId != closedContainerId)
					.forEach(containerId => {
						this.containers[containerId].containerClosed(closedContainerId);
					});
			});

		//close a dialog
		delegatedObserver(this.eventBlockLayerElem, 'click', '.tlmd-dialog-panel > .tlmd-panel > div.tlmd-panel-flexcontainer >  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-close')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				const closedDialogId = this.activeDialogId;
				this.closeDialog();

				//signal all containers that this dialog closed
				if (closedDialogId) {
					Object.keys(this.containers)
						.forEach(containerId => {
							this.containers[containerId].dialogClosed(closedDialogId);
						});
				}
			});

		//open a drawer based on 'data-drawer' attribute. The anchor must have the tlmd-action-open class
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-action-open')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				let drawerToOpen = evAndTrgt.target.dataset.drawer;
				let drawerTargetWidth = parseInt(evAndTrgt.target.dataset.width);
				this.slideOpen(drawerToOpen, drawerTargetWidth && drawerTargetWidth > 600 ? drawerTargetWidth : 600);
			});

		//shut the drawer that contains the target anchor. The anchor must have the tlmd-action-shut class 
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-action-shut')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				let drawerElem = <HTMLElement>evAndTrgt.target.closest('div.tlmd-panel');
				this.slideShut(drawerElem.dataset.cab, drawerElem.dataset.name);
			});

		//signals the container to pan the map 
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-panmap')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				this.getContainer(this.activeContainerId).panMap();
			});

		//signals the container to pan the timeline 
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-pantimeline')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				this.getContainer(this.activeContainerId).panTimeline();
			});

		//signals the container to export its data 
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-top  a.tlmd-export')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				this.getContainer(this.activeContainerId).export();
			});

		//make the section switches work 
		delegatedObserver(this.topElem, 'click', '.tlmd-container > div.tlmd-cabinet > div.tlmd-panel  div.tlmd-headers > div.tlmd-header.tlmd-selectors  a.tlmd-action-section')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				let drawerElem = <HTMLDivElement>evAndTrgt.target.closest('div.tlmd-panel');
				this.getContainer(this.activeContainerId).selectSection(drawerElem.dataset.cab, drawerElem.dataset.name, evAndTrgt.target.dataset.section);
			});

		//make foldout sections work  
		delegatedObserver(this.topElem, 'click', 'div.tlmd-foldout-container a.tlmd-foldout')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				let foldoutContainerElem = <HTMLDivElement>evAndTrgt.target.closest('div.tlmd-foldout-container');
				if (foldoutContainerElem) {
					foldoutContainerElem.classList.toggle('folded');
				}
			});

		//handlers equal to all div.tlmd-viewer-settings sections 
		//enable/disable viewers. The container gets called with the new state and for which viewer. The viewer is taken 
		//from the data-viewer attribute from the tlmd-viewer-settings container element
		delegatedObserver(this.topElem, 'change', 'div.tlmd-viewer-settings span.tlmd-enable-viewer input[type=checkbox]')
			.subscribe(evAndTrgt => {
				evAndTrgt.event.preventDefault();
				evAndTrgt.target.blur();
				let viewerSettingsContainerElem = <HTMLDivElement>evAndTrgt.target.closest('div.tlmd-viewer-settings');
				if (viewerSettingsContainerElem) {
					viewerSettingsContainerElem.dataset.viewer
					if (!(<HTMLInputElement>evAndTrgt.target).checked) {
						//fold the viewer settings
						viewerSettingsContainerElem.classList.add('folded');
					}
					this.getContainer(this.activeContainerId).enableVisualizer(viewerSettingsContainerElem.dataset.viewer, (<HTMLInputElement>evAndTrgt.target).checked);
				}
			});

	}
	private slideOpen = <(drawerKey: string, targetWidth: number) => void>throttle((drawerKey: string, targetWidth: number) => {
		let delta = targetWidth - this.getContainer(this.activeContainerId).getDrawer(drawerKey).getWidth();
		if (delta > 0) {
			this.getContainer(this.activeContainerId).slideDrawer(delta, drawerKey);
		}
	}, 250);

	private slideShut(cabinet: string, drawer: string): void {
		this.getContainer(this.activeContainerId).shutDrawer(cabinet, drawer);
	}

	private activateEventBlocker(): ContainerPanel {
		this.eventBlockLayerElem.classList.add('active');
		return this;
	}

	private deactivateEventBlocker(): ContainerPanel {
		this.eventBlockLayerElem.classList.remove('active');
		return this;
	}

	public closeDialog(): ContainerPanel {
		if (this.activeDialogId !== undefined) {
			this.dialogs[this.activeDialogId].onBlur();
			this.dialogs[this.activeDialogId].close();
			this.activeDialogId = undefined;
		}

		this.deactivateEventBlocker();
		Array.from(this.eventBlockLayerElem.children).forEach((dialogContainer: HTMLDivElement) => {
			dialogContainer.style.display = 'none';
		});
		return this;
	}

	public switchDialog(dialogId: string): Promise<ContainerPanel> {
		return new Promise<ContainerPanel>((resolve, reject) => {
			//check if index is within range and not yet active yet
			if (this.dialogDefinitions[dialogId]) {
				if (this.activeDialogId === undefined || dialogId === this.activeDialogId) {

					//display requested dialogContainer and hide all others
					Array.from(this.eventBlockLayerElem.children).forEach((dialogContainer: HTMLDivElement) => {
						dialogContainer.style.display = dialogContainer.dataset.dialog === dialogId ? 'block' : 'none';
					});

					this.activeDialogId = dialogId;
					let containerElem = this.getDialogContainerElement(this.activeDialogId);
					if (!containerElem.firstElementChild) {
						//no content for thisdialog  container yet
						this.dialogs[this.activeDialogId]
							.open()
							.then(dialog => {
								dialog.onFocus();
								this.dialogs[this.activeDialogId].onFocus();
								//now container has content
								this.activateEventBlocker();
								resolve(this);
								this.dialogs[this.activeDialogId].onFocus();
							});
					} else {
						//dialog already has content
						this.dialogs[this.activeDialogId].onFocus();
						this.activateEventBlocker();


						resolve(this);
						this.dialogs[this.activeDialogId].onFocus();
					}
				} else { //another dialog is still open
					reject(this)
				}
			} else {
				// illegal dialog id
				reject(this);
			}
		});
	}

	public switchContainer(containerId: string, initOnly = false): Promise<ContainerPanel> {

		return new Promise<ContainerPanel>((resolve, _reject) => {

			//check if index is within range and not yet active yet
			if (this.containerDefinitions[containerId]) {
				//display requested container and hide all others
				Array.from(this.topElem.children).forEach((container: HTMLDivElement) => {
					container.style.display = container.dataset.container === containerId && !initOnly ? 'flex' : 'none';
				});
				if (containerId !== this.activeContainerId) {
					if (this.containers[this.activeContainerId]) {
						this.containers[this.activeContainerId].onBlur();
					}
					this.activeContainerId = containerId;
					let containerElem = this.getContainerElement(this.activeContainerId);
					if (!containerElem.firstElementChild) {
						//no content for this container yet
						this.containers[this.activeContainerId]
							.open()
							.then(container => {
								//make sure that the first drawer is open.
								let panelState = Object.assign({}, container.getState());
								if (panelState.cabinets[0].width <= 0) {
									let initialContainerDefinition = this.containerDefinitions[this.activeContainerId];
									let width = initialContainerDefinition.cabinets[0].drawers[0].width;

									panelState.cabinets[0].drawers[0] = Object.assign({}, initialContainerDefinition.cabinets[0].drawers[0]);
									panelState.cabinets[0].drawers[0].width = width !== undefined && width > 0 ? width : DEFAULT_DRAWER_WIDTH;
									container.setState(panelState);

								}
								container.onFocus();
								this.containers[this.activeContainerId].onFocus();
								//now container has content
								resolve(this);
								this.containers[this.activeContainerId].onFocus();
							});
					} else {
						//container already has content
						this.containers[this.activeContainerId].onFocus();
						resolve(this);
						this.containers[this.activeContainerId].onFocus();
					}
				} else { //container already active
					this.containers[this.activeContainerId].onFocus();
					resolve(this)
				}
			} else {
				// illegal container id
				resolve(this);
			}
		});

	}
	public getActiveContainerId(): string {
		return this.activeContainerId;
	}
	public getContainerElement(containerId: string): HTMLDivElement {
		return <HTMLDivElement>this.topElem.querySelector(`div.tlmd-container.${containerId}`);
	}
	public getDialogContainerElement(dialogId: string): HTMLDivElement {
		return <HTMLDivElement>this.eventBlockLayerElem.querySelector(`div.tlmd-dialog-panel.${dialogId}`);
	}
	public getMovesUntilMouseUpObserver(): Observable<MouseEvent> {
		return this.mouseMovesUntilUpObs;
	}
	public getTouchEndObserver(): Observable<TouchEvent> {
		return this.touchEndObs;
	}
	public getMaxWidth(): number {
		return parseFloat(this.stageStyle.width) - this.options.marginLeft;
	}
	public getHeight(): number {
		return parseFloat(this.topStyle.height);
	}
	public getNorth(): number {
		return parseFloat(this.topStyle.top);
	}
	public getSouth(): number {
		return this.getNorth() + this.getHeight();
	}
	public setHeight(height: number): void {
		this.topElem.style.height = `${height}px`;
	}

	private fixOptions(options?: PanelOptions): void {
		this.options = {
			marginLeft: 20,
			bumperWidth: 34
		};
		if (options) {
			this.options.marginLeft = options.marginLeft === undefined ? this.options.marginLeft : options.marginLeft;
			this.options.bumperWidth = options.bumperWidth === undefined ? this.options.bumperWidth : options.bumperWidth;
		}
	}

	public getContainer(containerId?: string): Container {

		return this.containers[containerId ? containerId : this.activeContainerId];
	}

	public getContainerNodeSelector(containerId: string): string {
		return `${this.topSelector} > div.tlmd-container.${containerId}`;
	}
	public getDialogNodeSelector(dialogId: string): string {
		return `${ContainerPanel.SELECTOR_EVENT_BLOCKER_LAYER} > div.tlmd-dialog-panel.${dialogId}`;
	}
}

export class InspectionPanel extends ContainerPanel {
	private gMapWrapper: MapImplementationWrapper;

	constructor(topSelector: string, application: Application, stageSelector?: string) {
		super(topSelector, application, stageSelector);
		this.addContainer("top", "top", topContainerDefinition);


		getGMapImplementationWrapper({ apiKey: application.getGmapApiKey(), mapId: application.getGmapMapId(), version: 'weekly' }).then(wrapper => {
			this.gMapWrapper = wrapper;
		});
	};

	public getMapImplementationWrapper() {
		return this.gMapWrapper
	}

}

